'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '../_lib/utils';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { anticipate, motion } from 'motion/react';
import type { SanityImage } from '../types';
import { isValidRichText } from '../_lib/isValidRichText';
import { useRectTracker } from '../_lib/useRectTracker';
import { ComponentButton, type ComponentButtonProps } from '../Inputs/Button';
import { Container } from '../Container';
import { Media } from '../Media';
import { Text } from '../Text';
import { Video } from '../Video';

export type PrimaryHeroProps = {
	animate?: boolean;
	autoplayVideo?: string;
	backgroundImage?: SanityImage;
	backgroundVideo?: string;
	buttons?: ComponentButtonProps[];
	className?: string;
	copy?: ReactNode;
	image?: SanityImage;
	overline?: string;
	title: ReactNode;
	video?: string;
};

const countMax = 6;
export const sessionCount = atomWithStorage('sessions', 0);

export function PrimaryHero(props: PrimaryHeroProps) {
	const [count, setCount] = useAtom(sessionCount);
	const enabledAnimate = props.animate ?? false;
	const animate = enabledAnimate && count === 1 ? true : false;
	const contentRef = useRef<HTMLDivElement>(null);
	const contentRect = useRectTracker(contentRef);

	useEffect(() => {
		setCount(current => {
			return current < countMax ? current + 1 : 1;
		});
	}, [setCount]);

	return (
		<article
			className={cn('bg-background-primary relative z-50 pt-28 md:pt-36 lg:pt-[9rem]', props.className)}
			data-component='PrimaryHero'
		>
			{(props.backgroundImage || props.backgroundVideo) && (
				<motion.div
					key={animate}
					className={`absolute top-0 left-0 w-full pointer-events-none`}
					initial={animate ? { opacity: 0, y: '-4rem' } : {}}
					animate={animate ? { opacity: 1, y: 0 } : {}}
					transition={{
						delay: 0.2,
						duration: 1,
						ease: anticipate,
					}}
				>
					<Media className={`aspect-video [&_img]:h-full`} image={props.backgroundImage} video={props.backgroundVideo} priority />
					<div className={`w-full absolute top-0 left-0 bottom-0 bg-gradient-to-b from-background-primary/10 to-background-primary z-30`} />
				</motion.div>
			)}
			<div className={`flex flex-col gap-responsive-3xl relative z-40`}>
				<Container size='md'>
					<motion.div
						key={animate}
						className={`flex flex-col items-center gap-responsive-md text-center lg:pt-16`}
						ref={contentRef}
						// style={enableAnimation ? { opacity: 0, transform: 'translateY(-1rem)' } : {}}
						initial={animate ? { opacity: 0, y: '-1rem' } : {}}
						animate={animate ? { opacity: 1, y: 0 } : {}}
						transition={{
							delay: 1.6,
							duration: 1,
							ease: anticipate,
						}}
					>
						{props.overline && (
							<Text as='span' className='text-text-brand' styleType='label-md'>
								{props.overline}
							</Text>
						)}
						<Text as='h1' styleType='heading-2xl'>
							{props.title}
						</Text>
						{isValidRichText(props.copy) && (
							<Text className={`opacity-80`} styleType='body-lg'>
								{props.copy}
							</Text>
						)}

						{props.buttons && props.buttons.length > 0 && (
							<div className={`flex flex-wrap gap-4`}>
								{props.buttons.map((item, index) => (
									<ComponentButton key={index} {...item} />
								))}
							</div>
						)}
					</motion.div>
				</Container>
				{(props.image || props.autoplayVideo) && (
					<div className={`pointer-events-none`} style={animate ? { transform: `translateY(-${contentRect.height}px)` } : {}}>
						<motion.div
							key={animate}
							initial={animate ? { opacity: 0, scale: 0.9 } : {}}
							animate={animate ? { opacity: 1, scale: 1 } : {}}
							transition={{
								delay: 0.2,
								duration: 1,
								ease: anticipate,
							}}
						>
							<motion.div
								key={animate}
								className={`pointer-events-auto`}
								animate={animate ? { y: `${contentRect.height}px` } : {}}
								transition={{
									delay: 1.5,
									duration: 1,
									ease: anticipate,
								}}
							>
								<Video
									className={`rounded-lg overflow-hidden [&>div]:h-full [&_img]:object-contain! aspect-square md:aspect-[16/9]`}
									mediaAspectRatio={props.autoplayVideo ? '16/9' : undefined}
									autoplayVideo={props.autoplayVideo}
									image={props.image}
									size='unset'
									video={props.video}
									priority
								/>
							</motion.div>
						</motion.div>
					</div>
				)}
			</div>
		</article>
	);
}
